import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Ticket = Database['public']['Tables']['tickets']['Row'];
type TicketInsert = Database['public']['Tables']['tickets']['Insert'];
type TicketUpdate = Database['public']['Tables']['tickets']['Update'];
type FeedbackSubmission = Database['public']['Tables']['feedback_submissions']['Row'];
type FeedbackInsert = Database['public']['Tables']['feedback_submissions']['Insert'];

export const useTickets = () => {
  const queryClient = useQueryClient();

  const { data: tickets, isLoading: ticketsLoading } = useQuery({
    queryKey: ['tickets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Ticket[];
    }
  });

  const { data: feedbackSubmissions, isLoading: feedbackLoading } = useQuery({
    queryKey: ['feedback-submissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback_submissions')
        .select('*')
        .order('submitted_at', { ascending: false });
      
      if (error) throw error;
      return data as FeedbackSubmission[];
    }
  });

  const createTicket = useMutation({
    mutationFn: async (ticket: TicketInsert) => {
      const { data, error } = await supabase
        .from('tickets')
        .insert(ticket)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });

  const updateTicket = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TicketUpdate }) => {
      const { data, error } = await supabase
        .from('tickets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });

  const submitFeedback = useMutation({
    mutationFn: async (feedback: FeedbackInsert) => {
      const { data, error } = await supabase
        .from('feedback_submissions')
        .insert(feedback)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-submissions'] });
    }
  });

  const convertFeedbackToTicket = useMutation({
    mutationFn: async (feedbackId: string) => {
      const { data, error } = await supabase
        .rpc('convert_feedback_to_ticket', { feedback_id: feedbackId });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-submissions'] });
    }
  });

  return {
    tickets,
    feedbackSubmissions,
    isLoading: ticketsLoading || feedbackLoading,
    createTicket,
    updateTicket,
    submitFeedback,
    convertFeedbackToTicket
  };
};